import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { AiScoringService } from 'src/app/private/services/ai-scoring.service';
import { LocalStorageService } from 'src/app/utils/LocalStorageService';
import { ToastrService } from 'ngx-toastr';

@Component({
  selector: 'app-ai-scoring-guidelines',
  templateUrl: './ai-scoring-guidelines.component.html',
  styleUrls: ['./ai-scoring-guidelines.component.scss']
})
export class AiScoringGuidelinesComponent implements OnInit {

  loading = true;
  saving = false;
  resetting = false;
  triggeringCron = false;

  guideline: any = null;

  // Form model
  systemPromptAddon = '';
  customRules = '';
  weights: any = {
    recency: 30,
    communication_volume: 20,
    meeting_engagement: 20,
    status_progression: 15,
    task_completion: 10,
    notes_sentiment: 5
  };
  highValueStatuses: string[] = [];
  lowValueStatuses: string[] = [];

  // Status chip input
  newHighStatus = '';
  newLowStatus = '';

  // Weight total validation
  get weightTotal(): number {
    return Object.values(this.weights).reduce((sum: any, v: any) => sum + (v || 0), 0) as number;
  }

  get isWeightValid(): boolean {
    return this.weightTotal === 100;
  }

  weightCategories = [
    { key: 'recency', label: 'Recency of Activity', icon: 'fa-clock', color: '#6366f1', description: 'How recently did the lead interact?' },
    { key: 'communication_volume', label: 'Communication Volume', icon: 'fa-phone', color: '#0ea5e9', description: 'Number of calls, emails & messages' },
    { key: 'meeting_engagement', label: 'Meeting Engagement', icon: 'fa-calendar-alt', color: '#10b981', description: 'Meetings attended and scheduled' },
    { key: 'status_progression', label: 'Status Progression', icon: 'fa-exchange-alt', color: '#f59e0b', description: 'How far along the pipeline' },
    { key: 'task_completion', label: 'Task Completion', icon: 'fa-check-square', color: '#8b5cf6', description: 'Counselor tasks completed on time' },
    { key: 'notes_sentiment', label: 'Notes Sentiment', icon: 'fa-comments', color: '#ec4899', description: 'Positive vs negative sentiment in notes' }
  ];

  constructor(
    private aiScoringService: AiScoringService,
    private localStorage: LocalStorageService,
    private toast: ToastrService
  ) {}

  ngOnInit(): void {
    this.loadGuideline();
  }

  loadGuideline(): void {
    const payload = { hospital_id: this.localStorage.getHospitalId() };
    this.aiScoringService.getAiScoringGuideline(payload).subscribe({
      next: (data) => {
        this.guideline = data;
        this.populateForm(data);
        this.loading = false;
      },
      error: (err) => {
        console.error('Error loading guideline:', err);
        this.toast.error('Failed to load AI scoring guideline', 'Error');
        this.loading = false;
      }
    });
  }

  populateForm(data: any): void {
    this.systemPromptAddon = data.system_prompt_addon || '';
    this.customRules = data.custom_rules || '';
    this.weights = { ...(data.weights || { recency: 30, communication_volume: 20, meeting_engagement: 20, status_progression: 15, task_completion: 10, notes_sentiment: 5 }) };
    this.highValueStatuses = Array.isArray(data.high_value_statuses) ? [...data.high_value_statuses] : [];
    this.lowValueStatuses = Array.isArray(data.low_value_statuses) ? [...data.low_value_statuses] : [];
  }

  saveGuideline(): void {
    if (!this.isWeightValid) {
      this.toast.warning('Weights must total exactly 100%', 'Validation Error');
      return;
    }

    this.saving = true;
    const payload = {
      organization_id: this.localStorage.getHospitalId(),
      user_id: this.localStorage.getUserId(),
      system_prompt_addon: this.systemPromptAddon,
      custom_rules: this.customRules,
      weights: this.weights,
      high_value_statuses: this.highValueStatuses,
      low_value_statuses: this.lowValueStatuses
    };

    this.aiScoringService.updateAiScoringGuideline(payload).subscribe({
      next: (res) => {
        this.saving = false;
        this.guideline = res.guideline;
        this.toast.success('AI scoring guidelines updated successfully', 'Success');
      },
      error: (err) => {
        this.saving = false;
        this.toast.error('Failed to save guidelines', 'Error');
      }
    });
  }

  resetToDefault(): void {
    this.resetting = true;
    const payload = { organization_id: this.localStorage.getHospitalId() };
    this.aiScoringService.resetAiScoringGuideline(payload).subscribe({
      next: (res) => {
        this.resetting = false;
        this.guideline = res.guideline;
        this.populateForm(res.guideline);
        this.toast.success('Guidelines restored to default', 'Success');
      },
      error: () => {
        this.resetting = false;
        this.toast.error('Failed to reset guidelines', 'Error');
      }
    });
  }

  // Status chip management
  addHighStatus(): void {
    const val = this.newHighStatus.trim().toLowerCase();
    if (val && !this.highValueStatuses.includes(val)) {
      this.highValueStatuses.push(val);
    }
    this.newHighStatus = '';
  }

  removeHighStatus(status: string): void {
    this.highValueStatuses = this.highValueStatuses.filter(s => s !== status);
  }

  addLowStatus(): void {
    const val = this.newLowStatus.trim().toLowerCase();
    if (val && !this.lowValueStatuses.includes(val)) {
      this.lowValueStatuses.push(val);
    }
    this.newLowStatus = '';
  }

  removeLowStatus(status: string): void {
    this.lowValueStatuses = this.lowValueStatuses.filter(s => s !== status);
  }

  getWeightColor(key: string): string {
    const cat = this.weightCategories.find(c => c.key === key);
    return cat?.color || '#6366f1';
  }

  getLastUpdated(): string {
    if (!this.guideline?.updatedAt && !this.guideline?.updated_at) return 'Never';
    const d = new Date(this.guideline.updatedAt || this.guideline.updated_at);
    return d.toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', dateStyle: 'medium', timeStyle: 'short' });
  }

  getScoreLabel(score: number): { label: string, color: string } {
    if (score >= 80) return { label: '🔥 Hot Lead', color: '#ef4444' };
    if (score >= 60) return { label: '🌡️ Warm Lead', color: '#f97316' };
    if (score >= 40) return { label: '🌤️ Lukewarm', color: '#eab308' };
    if (score >= 20) return { label: '❄️ Cold Lead', color: '#0ea5e9' };
    return { label: '🧊 Inactive', color: '#6b7280' };
  }

  hexToRgb(hex: string): string {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    if (!result) return '99, 102, 241';
    return `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}`;
  }
}
